import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Flex,
  Icon,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { List, Folder, Copy } from "lucide-react";
import ChatList from "./chat-list";
import { useDashboard } from "@root/hooks/useDashboard";
import {
  CHATBOT_TYPE,
  LAW_TYPES,
  LEGAL_CHATMODE,
  PAGINATION_DEFAULTS,
} from "@root/constants";
import { ChatIcon } from "@chakra-ui/icons";
import { DrawingIcon } from "@root/assets/icons/drawingicon";
import { DrawingTableIcon } from "@root/assets/icons/drawing-icon";
import { FolderIcon } from "@root/assets/icons/foldericon";
import { MessageIcon } from "@root/assets/icons/messageIcon";
import { Paginate } from "@root/components/pagination";

interface ChatSectionProps {}

interface TabItem {
  label: string;
  icon: React.ElementType;
  type: CHATBOT_TYPE;
}
export const ChatSection: React.FC<ChatSectionProps> = () => {
  const { t } = useTranslation("common");

  const [activeTab, setActiveTab] = useState(0);
  const [selectedToggle, setSelectedToggle] = useState(
    LEGAL_CHATMODE.WEB_SEARCH.toString()
  );
  const [page, setPageNo] = useState(1);

  const {
    eachChatbotChats,
    eachchatbotChatsCount,
    fetchEachChatbotChat,
    eachChatbotChatsLoading,
  }: any = useDashboard();

  const tabItems: TabItem[] = [
    {
      label: t("translate.LegalChatbot"),
      icon: MessageIcon,
      type: CHATBOT_TYPE.LAW_CHATBOT,
    },
    {
      label: t("chatHistory.projectDocumentChatbot"),
      icon: FolderIcon,
      type: CHATBOT_TYPE.PROJECT_DOCUMENT_CHATBOT,
    },
    {
      label: t("chatHistory.drawingAnalysisChatbot"),
      icon: DrawingIcon,
      type: CHATBOT_TYPE.DRAWING_ANALYSIS_CHATBOT,
    },
    {
      label: t("chatHistory.multiDocumentChatbot"),
      icon: DrawingTableIcon,
      type: CHATBOT_TYPE.MULTI_DOCUMENT_CHATBOT,
    },
  ];

  // Unified fetch function
  const fetchChats = useCallback(
    (type: CHATBOT_TYPE, page: number = 1, toggle?: string) => {
      const payload: any = {
        type,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page,
      };

      if (type === CHATBOT_TYPE.LAW_CHATBOT && toggle) {
        payload.source =
          toggle === LEGAL_CHATMODE.WEB_SEARCH.toString()
            ? LEGAL_CHATMODE.WEB_SEARCH.toString()
            : LEGAL_CHATMODE.LAW_INFO_CENTER.toString();
      }

      fetchEachChatbotChat(payload);
    },
    [fetchEachChatbotChat]
  );

  // Fetch on tab change or toggle change
  useEffect(() => {
    const currentType = tabItems[activeTab].type;
    fetchChats(currentType, 1, selectedToggle);
    setPageNo(1);
  }, [selectedToggle, activeTab]);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    const currentType = tabItems[activeTab].type;
    fetchChats(currentType, newPage, selectedToggle);
  };

  return (
    <Box maxW="100%" h="100%" display="flex" flexDirection="column">
      <Tabs
        variant="unstyled"
        index={activeTab}
        onChange={setActiveTab}
        display="flex"
        flexDirection="column"
        h="100%"
      >
        <Flex
          align="flex-start"
          justify="space-between"
          mb={5}
          direction={{ base: "row", md: "column" }}
          gap={{ base: 0, md: 3 }}
          flexShrink={0}
        >
          <TabList gap={3}>
            {tabItems.map((tab, index) => (
              <Tab
                key={index}
                display="flex"
                alignItems="center"
                gap={2}
                borderRadius="md"
                fontWeight="medium"
                bg="#EBEBEB"
                color="black"
                transition="all 0.2s"
                _selected={{ bg: "black", color: "white" }}
              >
                <Icon as={tab.icon} boxSize={6} />
                {tab.label}
              </Tab>
            ))}
          </TabList>

          {/* Toggle for Law Chatbot only */}
          {tabItems[activeTab]?.type === CHATBOT_TYPE.LAW_CHATBOT && (
            <Flex
              justify={{ base: "flex-end", md: "flex-end" }}
              alignSelf="flex-end"
            >
              <Tabs
                variant="unstyled"
                isFitted={false}
                index={
                  selectedToggle === LEGAL_CHATMODE.WEB_SEARCH.toString()
                    ? 0
                    : 1
                }
                onChange={(index) =>
                  setSelectedToggle(
                    index === 0
                      ? LEGAL_CHATMODE.WEB_SEARCH.toString()
                      : LEGAL_CHATMODE.LAW_INFO_CENTER.toString()
                  )
                }
              >
                <TabList
                  background="#CDD6DC3D"
                  borderRadius="100px"
                  display="flex"
                  whiteSpace="nowrap"
                  boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.1), 0px 0px 4px 0px rgba(0, 0, 0, 0.05)"
                  border="1px solid #E6E6E6"
                >
                  <Tab
                    fontWeight="600"
                    fontSize="16px"
                    color="#798692"
                    _selected={{
                      color: "#0FABBE",
                      bg: "white",
                      borderRadius: "100px",
                    }}
                    _focus={{ boxShadow: "none" }}
                  >
                    {t("translate.webSearch")}
                  </Tab>
                  <Tab
                    fontWeight="600"
                    fontSize="16px"
                    color="#798692"
                    _selected={{
                      color: "#0FABBE",
                      bg: "white",
                      borderRadius: "100px",
                    }}
                    _focus={{ boxShadow: "none" }}
                  >
                    {t("translate.lawInfoCentre")}
                  </Tab>
                </TabList>
              </Tabs>
            </Flex>
          )}
        </Flex>

        <TabPanels
          mt={5}
          p={0}
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
        >
          {tabItems.map((tab, index) => {
            const isActiveTab = index === activeTab;
            return (
              <TabPanel
                key={index}
                p={0}
                h="100%"
                display="flex"
                flexDirection="column"
              >
                {eachChatbotChatsLoading && isActiveTab ? (
                  <Center p={8} flex={1}>
                    <Spinner size="lg" color="blue.500" thickness="4px" />
                  </Center>
                ) : (
                  <>
                    <Box
                      flex={1}
                      overflowY="auto"
                      minHeight={0}
                      css={{
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#D1D5DB",
                          borderRadius: "3px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#9CA3AF",
                        },
                      }}
                    >
                      <ChatList
                        chats={eachChatbotChats}
                        count={eachchatbotChatsCount}
                        selectedToggle={selectedToggle}
                        onPageChange={handlePageChange}
                        page={page}
                        showPagination={false}
                      />
                    </Box>
                    <Flex flexShrink={0} justifyContent="center" mt={4}>
                      <Paginate
                        page={page}
                        count={eachchatbotChatsCount ?? []}
                        pageSize={PAGINATION_DEFAULTS?.PAGESIZE}
                        onPageChange={handlePageChange}
                      />
                    </Flex>
                  </>
                )}
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
